import { useState } from 'react';
import { X, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { PrivacyPolicyModal } from '@/components/PrivacyPolicyModal';
import { SectionId } from '@/components/OnePager';
import { cn } from '@/lib/utils';

interface NavigationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  // Authenticated user props
  isAuthenticated?: boolean;
  activeSection?: SectionId;
  onNavigate?: (sectionId: SectionId) => void;
  onSignOut?: () => void;
  // Public user props
  onSignUp?: () => void;
  onSignIn?: () => void;
  hideSignUp?: boolean;
  hideSignIn?: boolean;
}

const sectionItems: { id: SectionId; icon: string; labelKey: string }[] = [
  { id: 'drink-log', icon: '➕', labelKey: 'dashboardTitle' },
  { id: 'rewards', icon: '🔥', labelKey: 'rewardsTitle' },
  { id: 'leaderboard', icon: '🏆', labelKey: 'leaderboardTitle' },
  { id: 'profile', icon: '👤', labelKey: 'profileTitle' },
];

export function NavigationDrawer({
  isOpen,
  onClose,
  isAuthenticated = false,
  activeSection,
  onNavigate,
  onSignOut,
  onSignUp,
  onSignIn,
  hideSignUp,
  hideSignIn,
}: NavigationDrawerProps) {
  const [guideOpen, setGuideOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const { t, language, setLanguage } = useLanguage();

  const handleClose = () => {
    onClose();
    setGuideOpen(false);
    setContactOpen(false);
    setExpandedFaq(null);
  };

  const handleSectionNavigate = (sectionId: SectionId) => {
    handleClose();
    onNavigate?.(sectionId);
  };

  const handleSignOut = () => {
    handleClose();
    onSignOut?.();
  };

  const handleSignUp = () => {
    handleClose();
    onSignUp?.();
  };

  const handleSignIn = () => {
    handleClose();
    onSignIn?.();
  };

  const toggleFaqItem = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // FAQ/Guide items
  const faqItems = [
    { question: t('faqQ1'), answer: t('faqA1') },
    { question: t('faqQ2'), answer: t('faqA2') },
    { question: t('faqQ3'), answer: t('faqA3') },
    { question: t('faqQ4'), answer: t('faqA4') },
    { question: t('faqQ5'), answer: t('faqA5') },
  ];

  if (!isOpen) return null;

  return (
    <>
      {/* Drawer overlay - CSS Grid layout with explicit rows */}
      <div 
        className="fixed inset-0 z-50 bg-background animate-in fade-in duration-200"
        style={{
          display: 'grid',
          gridTemplateRows: '56px 1fr auto',
          height: 'calc(100dvh - 64px - env(safe-area-inset-bottom, 0px))',
        }}
      >
        
        {/* ROW 1: Sticky Header - non-scrollable */}
        <header className="border-b border-border bg-background">
          <div className="h-full max-w-lg mx-auto px-4 flex items-center justify-between">
            <span className="text-base font-serif font-bold">💯 drinkov</span>
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-muted/50 transition-colors"
              aria-label="Close menu"
            >
              <X className="h-5 w-5 text-muted-foreground" />
            </button>
          </div>
        </header>

        {/* ROW 2: Scrollable Content Area - only this scrolls */}
        <div className="overflow-y-auto">
          <div className="px-4 py-3 max-w-lg mx-auto">
            
            {/* Primary Navigation (authenticated only) */}
            {isAuthenticated && onNavigate && (
              <div className="grid grid-cols-2 gap-2 mb-3">
                {sectionItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSectionNavigate(item.id)}
                    className={cn(
                      "flex items-center gap-2 py-2 px-3 rounded-lg text-left transition-colors text-sm font-medium",
                      activeSection === item.id 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted/50 hover:bg-muted text-foreground'
                    )}
                  >
                    <span className="text-base">{item.icon}</span>
                    <span>{t(item.labelKey)}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Public user auth actions */}
            {!isAuthenticated && (
              <div className="space-y-2 mb-3">
                {!hideSignUp && (
                  <button
                    onClick={handleSignUp}
                    className="w-full flex items-center justify-center py-2 px-4 rounded-lg bg-primary text-primary-foreground font-medium text-sm transition-colors hover:bg-primary/90"
                  >
                    {t('landingCta')}
                  </button>
                )}
                {!hideSignIn && (
                  <button
                    onClick={handleSignIn}
                    className="w-full flex items-center justify-center py-2 px-4 rounded-lg bg-muted/50 hover:bg-muted text-foreground font-medium text-sm transition-colors"
                  >
                    {t('authSignIn')}
                  </button>
                )}
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-border/50 my-2" />

            {/* Secondary Actions */}
            <div className="space-y-0.5">
              {/* Language Switch - inline compact */}
              <div className="flex items-center justify-between py-2 px-3 rounded-lg">
                <span className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>🌍</span>
                  <span>{t('menuLanguage')}</span>
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setLanguage('sk')}
                    className={cn(
                      "px-2 py-0.5 rounded text-xs font-medium transition-colors",
                      language === 'sk' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    SK
                  </button>
                  <span className="text-muted-foreground/50">|</span>
                  <button
                    onClick={() => setLanguage('en')}
                    className={cn(
                      "px-2 py-0.5 rounded text-xs font-medium transition-colors",
                      language === 'en' 
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    EN
                  </button>
                </div>
              </div>

              {/* ℹ️ Guide - collapsible */}
              <Collapsible open={guideOpen} onOpenChange={setGuideOpen}>
                <CollapsibleTrigger className="w-full flex items-center justify-between py-2 px-3 rounded-lg text-left hover:bg-muted/50 transition-colors">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <span>ℹ️</span>
                    <span>{t('menuFaq')}</span>
                  </span>
                  {guideOpen ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent className="px-3 pt-1 pb-2 space-y-0.5">
                  {faqItems.map((item, index) => (
                    <div key={index} className="border-b border-border/30 last:border-0">
                      <button
                        onClick={() => toggleFaqItem(index)}
                        className="w-full flex items-start justify-between py-1.5 text-left"
                      >
                        <span className="text-xs text-foreground pr-3">{item.question}</span>
                        {expandedFaq === index ? (
                          <ChevronUp className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
                        ) : (
                          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
                        )}
                      </button>
                      {expandedFaq === index && (
                        <p className="text-xs text-muted-foreground pb-2 animate-in fade-in slide-in-from-top-1 duration-200 whitespace-pre-line">
                          {item.answer}
                        </p>
                      )}
                    </div>
                  ))}
                </CollapsibleContent>
              </Collapsible>

              {/* 📩 Kontakt & Podpora - collapsible */}
              <Collapsible open={contactOpen} onOpenChange={setContactOpen}>
                <CollapsibleTrigger className="w-full flex items-center justify-between py-2 px-3 rounded-lg text-left hover:bg-muted/50 transition-colors">
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <span>📩</span>
                    <span>{t('menuContact')}</span>
                  </span>
                  {contactOpen ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </CollapsibleTrigger>
                <CollapsibleContent className="px-3 pt-1 pb-2 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {t('infoPanelWelcome')}{' '}
                    <a 
                      href="https://revolut.me/petler" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="font-semibold text-primary hover:underline inline-flex items-center gap-0.5"
                    >
                      Revolut
                      <ExternalLink className="h-2.5 w-2.5" />
                    </a>
                    {' '}{t('infoPanelOr')}{' '}
                    <span className="font-semibold text-foreground">💯 Drinks in 365 Days</span>
                    {' '}{t('infoPanelWithFriends')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{t('infoPanelContact')}</span>{' '}
                    <a 
                      href="mailto:peter@tugedr.com" 
                      className="text-primary hover:underline"
                    >
                      peter@tugedr.com
                    </a>
                  </p>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        </div>

        {/* ROW 3: Sticky Footer - non-scrollable, always visible above bottom nav */}
        <footer className="border-t border-border bg-background">
          <div className="px-4 py-3 max-w-lg mx-auto space-y-0.5">
            {/* Privacy Policy */}
            <button
              onClick={() => setPrivacyModalOpen(true)}
              className="w-full flex items-center gap-2 py-1.5 px-3 rounded-lg text-left hover:bg-muted/50 transition-colors"
            >
              <span>🛡</span>
              <span className="text-xs text-muted-foreground">{t('menuPrivacyPolicy')}</span>
            </button>

            {/* Sign Out (authenticated only) */}
            {isAuthenticated && onSignOut && (
              <button
                onClick={handleSignOut}
                className="w-full flex items-center gap-2 py-1.5 px-3 rounded-lg text-left hover:bg-destructive/10 transition-colors"
              >
                <span>🚪</span>
                <span className="text-xs text-destructive font-medium">{t('signOut')}</span>
              </button>
            )}
          </div>
        </footer>
      </div>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal open={privacyModalOpen} onOpenChange={setPrivacyModalOpen} />
    </>
  );
}
